// Forma llenar datos cliente

import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  Card,
  CardContent,
  MenuItem,
  TextField,
  Typography,
  Grid
} from '@mui/material';
import SiteList from './SiteList';
import {
  FIELD_LABELS,
  emailValidator,
  nameValidator,
  numbersOnlyValidator,
  phoneValidator,
  requiredValidator,
  rfcValidator,
  streetNameValidator,
  validateField
} from '../validators';
import { MEXICAN_STATES } from '../states'
import { getAddressInfoForZipCode } from '../sepomex.js';
import { apiFetch } from '../fetchWrapper';
import { getServiceTypes } from './ServiceType';

export default function ClientForm({ client, onCancel, onSave, fromQuotation = false }) {

  const initialForm = {
    serviceType: 'OTHER',
    rfc: '',
    firstname: '',
    lastnameFirst: '',
    lastnameSecond: '',
    email: '',
    companyName: '',
    phoneNumber: '',
    mobileNumber: '',
    street: '',
    neighborhood: '',
    municipality: '',
    city: '',
    zipCode: '',
    state: '',
    country: 'Mexico',
  };

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [neighborhoods, setNeighborhoods] = useState([]);
  const hasErrors = Object.keys(errors).length > 0;

  const requiredFields = [
    'companyName',
    'street',
    'city',
    'municipality',
    'zipCode',
    'state',
    'firstname',
    'lastnameFirst',
    'email',
    'phoneNumber',
    'mobileNumber',
    'rfc',
  ];

  const requiredFieldsFilled = requiredFields.every(
    (field) => Boolean(form[field])
  );

  useEffect(() => {
    if (!client) return;
    setNeighborhoods(client.address?.neighborhood ? [client.address.neighborhood] : []);
    setForm({
      firstname: client.contactPerson?.firstname || '',
      lastnameFirst: client.contactPerson?.lastnameFirst || '',
      lastnameSecond: client.contactPerson?.lastnameSecond || '',
      email: client.contactPerson?.email || '',
      phoneNumber: client.contactPerson?.phoneNumber || '',
      mobileNumber: client.contactPerson?.mobileNumber || '',
      companyName: client.companyName || '',
      rfc: client.rfc || '',
      serviceType: client.serviceType || 'OTHER',
      street: client.address.street || '',
      municipality: client.address.municipality || '',
      city: client.address.city || '',
      neighborhood: client.address.neighborhood || '',
      zipCode: client.address.zipCode || '',
      state: client.address.state || '',
      country: client.address.country || 'Mexico',
    });
  }, [client]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const isNew = !client || fromQuotation;
    const clientId = isNew ? null : client?.id ?? null;
    const addressId = isNew ? null : client?.address?.id ?? null;
    const payload = {
      id: clientId,
      companyName: form.companyName,
      serviceType: form.serviceType || 'OTHER',
      rfc: form.rfc,
      contactPerson: {
        id: client?.contactPerson?.id || 0,
        firstname: form.firstname,
        lastnameFirst: form.lastnameFirst,
        lastnameSecond: form.lastnameSecond,
        email: form.email,
        phoneNumber: form.phoneNumber,
        mobileNumber: form.mobileNumber,
      },
      address: {
        id: addressId,
        street: form.street,
        neighborhood: form.neighborhood,
        city: form.city,
        municipality: form.municipality,
        state: form.state,
        zipCode: form.zipCode,
        country: form.country || 'Mexico',
      },
    };

    try {
      const method = isNew ? 'POST' : 'PUT';
      const url = isNew
        ? `/api/clients`
        : `/api/clients/${client.id}`;
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!res) return;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const savedClient = await res.json();
      onSave(savedClient);

    } catch (err) {
      console.error('Failed to save client', err);
      setError(err.message || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors(prev => {
      const next = { ...prev };
      let error = null;

      if (name === 'email') {
        error = validateField(value, [
          requiredValidator,
          emailValidator
        ]);
      }

      if (name === 'street') {
        error = validateField(value, [
          requiredValidator,
          streetNameValidator
        ]);
      }

      if (name === 'companyName' || name === 'firstname' || name === 'lastnameFirst' || name === 'lastnameSecond' ||
        name === 'municipality' || name === 'city' || name === 'neighborhood') {
        error = validateField(value, [
          requiredValidator,
          (v) => nameValidator(v, FIELD_LABELS[name])
        ]);
      }

      if (name === 'phoneNumber' || name === 'mobileNumber') {
        error = validateField(value, [
          requiredValidator,
          phoneValidator
        ]);
      }

      if (name === 'rfc') {

        const clean = String(value).toUpperCase().slice(0, 13);
        setForm(prev => ({ ...prev, [name]: clean }));

        if (clean.length < 12) {
          delete next[name];
          return next;
        }

        const error = rfcValidator(clean);

        if (error) next[name] = error;
        else delete next[name];

        return next;
      }

      if (name === 'zipCode') {
        error = validateField(value, [
          requiredValidator,
          numbersOnlyValidator
        ]);
      }

      if (error) {
        next[name] = error;
      } else {
        delete next[name];
      }
      return next;
    });
  };

  const handleCancel = () => {
    setForm(initialForm);
    onCancel();
  }

  useEffect(() => {
    const zip = form.zipCode;

    const error = validateField(zip, [
      requiredValidator,
      numbersOnlyValidator
    ]);

    if (!error && zip.length === 5) {
      getAddressInfoForZipCode(zip).then((data) => {
        if (data) {
          if (data.city) {
            setForm((prev) => ({ ...prev, city: data.city }));
          }
          if (data.state) {
            setForm((prev) => ({ ...prev, state: data.state }));
          }
          if (data.municipality) {
            setForm((prev) => ({ ...prev, municipality: data.municipality }));
          }
          if (data.neighborhoods?.length > 0) {
            setNeighborhoods(data.neighborhoods);
            setForm((prev) => ({
              ...prev,
              neighborhood: prev.neighborhood || data.neighborhoods[0],
            }));
          }
        }
      });
    }
  }, [form.zipCode]);

  return (
    <Card sx={{
      width: "100%",
      margin: '1rem auto',
      padding: 1,
      backgroundColor: '#f9f9f9',
    }}>

      <CardContent sx={{ pt: 2 }}>
        <Typography variant="subtitle1"
          sx={{
            mb: 2,
            fontSize: '16px',
            fontWeight: 600
          }}>
          Registro de cliente
        </Typography>

        <form onSubmit={handleSubmit}>

          <Box sx={{ mt: 1, mb: 2 }}>

            <Typography variant='body1' sx={{ mb: 1 }} >
              Datos de la empresa
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Nombre del cliente"
                  name="companyName"
                  error={!!errors.companyName}
                  helperText={errors.companyName}
                  value={form.companyName}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select label="Tipo de servicio"
                  name="serviceType"
                  value={form.serviceType}
                  onChange={handleChange}
                  required
                >
                  {
                    getServiceTypes().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  }
                </TextField>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant='body1' sx={{ mb: 1 }} >
              Contacto
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Nombre de persona de contacto"
                  name="firstname"
                  value={form.firstname}
                  onChange={handleChange}
                  error={!!errors.firstname}
                  helperText={errors.firstname}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Apellido Paterno"
                  name="lastnameFirst"
                  value={form.lastnameFirst}
                  onChange={handleChange}
                  error={!!errors.lastnameFirst}
                  helperText={errors.lastnameFirst}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Apellido Materno"
                  name="lastnameSecond"
                  value={form.lastnameSecond}
                  onChange={handleChange}
                  error={!!errors.lastnameSecond}
                  helperText={errors.lastnameSecond}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Teléfono"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 13 } }}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Teléfono móvil"
                  name="mobileNumber"
                  value={form.mobileNumber}
                  error={!!errors.mobileNumber}
                  helperText={errors.mobileNumber}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 13 } }}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Email de contacto"
                  name="email"
                  value={form.email}
                  error={!!errors.email}
                  helperText={errors.email}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="RFC"
                  name="rfc"
                  value={form.rfc}
                  error={!!errors.rfc}
                  helperText={errors.rfc}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 13 } }}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 1, mb: 2 }}>

            <Typography variant='body1' sx={{ mb: 1 }} >
              Dirección
            </Typography>

            <Grid container spacing={2}>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Calle y número"
                  name="street"
                  value={form.street}
                  error={!!errors.street}
                  helperText={errors.street}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 255 } }}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Código Postal"
                  name="zipCode"
                  error={!!errors.zipCode}
                  helperText={errors.zipCode}
                  value={form.zipCode}
                  onChange={handleChange}
                  fullWidth
                  slotProps={{ htmlInput: { maxLength: 5 } }}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {neighborhoods.length > 0 ? (
                  <TextField
                    select
                    label="Colonia"
                    name="neighborhood"
                    value={form.neighborhood}
                    onChange={handleChange}
                    error={!!errors.neighborhood}
                    helperText={errors.neighborhood}
                    fullWidth
                    required
                  >
                    <MenuItem value="">
                      <em>Seleccionar colonia</em>
                    </MenuItem>

                    {neighborhoods.map((colonia) => (
                      <MenuItem key={colonia} value={colonia}>
                        {colonia}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    label="Colonia"
                    name="neighborhood"
                    value={form.neighborhood}
                    onChange={handleChange}
                    error={!!errors.neighborhood}
                    helperText={
                      errors.neighborhood ||
                      'Escribe la colonia manualmente'
                    }
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                    fullWidth
                    required
                  />
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Ciudad"
                  name="city"
                  value={form.city}
                  error={!!errors.city}
                  helperText={errors.city}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Alcaldía/Municipio"
                  name="municipality"
                  error={!!errors.municipality}
                  helperText={errors.municipality}
                  value={form.municipality}
                  onChange={handleChange}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { maxLength: 100 }
                  }}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  label="Estado"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  fullWidth
                  required
                >
                  {MEXICAN_STATES.map((state) => (
                    <MenuItem key={state.code} value={state.name}>
                      {state.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>

            <Button
              type="button"

              color="error"
              onClick={handleCancel}
              disabled={saving}>
              Cancelar
            </Button>

            <Button
              sx={{
                backgroundColor: "#3f74b1",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#364f6b"
                }
              }}
              type="submit"
              disabled={saving || hasErrors || !requiredFieldsFilled}
              variant="contained"
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>


          </Box>
        </form >
        <SiteList clientId={client?.id} />
      </CardContent >
    </Card >
  );
}